import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import ReactDOM from "react-dom";
import { cn } from "@/shared/lib/utils";
import styles from "./Modal.module.scss";
const SIZE_CLASS = {
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
    xl: styles.sizeXl,
};
/**
 * Portal-based modal. Owns its overlay; clicking the backdrop or pressing
 * ESC closes via `onClose`. Body scroll is locked while open. Focus is
 * delegated to the first focusable element inside on open, and restored to
 * the previously focused element on close.
 */
export const Modal = ({ opened, onClose, title, size = "md", closeOnBackdropClick = true, closeOnEscape = true, hideCloseButton = false, children, ariaLabel, className, }) => {
    const panelRef = React.useRef(null);
    const previousFocusRef = React.useRef(null);
    const titleId = React.useId();
    // Свежие onClose/closeOnEscape держим в ref'ах, чтобы эффект ниже зависел
    // ТОЛЬКО от `opened` и не перезапускался на каждый рендер родителя. Иначе при
    // инлайновом `onClose` (новая функция каждый рендер) эффект перезапускался бы
    // на каждый ввод символа в поле внутри модалки и уводил фокус на первый
    // фокусируемый элемент (× в шапке) — ломая ввод.
    const onCloseRef = React.useRef(onClose);
    const closeOnEscapeRef = React.useRef(closeOnEscape);
    React.useEffect(() => {
        onCloseRef.current = onClose;
        closeOnEscapeRef.current = closeOnEscape;
    });
    // ESC + focus restoration + body scroll lock. Только при открытии/закрытии.
    React.useEffect(() => {
        if (!opened)
            return;
        previousFocusRef.current = document.activeElement;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape" && closeOnEscapeRef.current) {
                e.stopPropagation();
                onCloseRef.current();
            }
        };
        document.addEventListener("keydown", onKey);
        // Move focus into the panel. Приоритет — элемент с [data-autofocus]
        // (напр. основное поле ввода), иначе первый фокусируемый (обычно × в шапке).
        requestAnimationFrame(() => {
            const panel = panelRef.current;
            if (!panel)
                return;
            const focusable = panel.querySelector('[data-autofocus]:not([disabled])') ??
                panel.querySelector('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
            (focusable ?? panel).focus({ preventScroll: true });
        });
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
            previousFocusRef.current?.focus({ preventScroll: true });
        };
    }, [opened]);
    if (!opened || typeof document === "undefined")
        return null;
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && closeOnBackdropClick) {
            onClose();
        }
    };
    return ReactDOM.createPortal(_jsx("div", { className: cn(styles.backdrop, opened && styles.backdropOpen), onMouseDown: handleBackdropClick, role: "presentation", children: _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-labelledby": title ? titleId : undefined, "aria-label": !title ? ariaLabel : undefined, tabIndex: -1, className: cn(styles.panel, SIZE_CLASS[size], className), children: [title && (_jsxs("header", { className: styles.header, children: [_jsx("h2", { id: titleId, className: styles.title, children: title }), !hideCloseButton && (_jsx("button", { type: "button", className: styles.close, onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", "aria-hidden": "true", children: _jsx("path", { d: "M3 3l10 10M13 3L3 13" }) }) }))] })), _jsxs("div", { className: cn(styles.body, !title && styles.bodyNoTitle), children: [!title && !hideCloseButton && (_jsx("button", { type: "button", className: styles.close, onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", style: { position: "absolute", top: 12, right: 12 }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", "aria-hidden": "true", children: _jsx("path", { d: "M3 3l10 10M13 3L3 13" }) }) })), children] })] }) }), document.body);
};
